import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { analytics } from '../services/analytics';
import { ConsentSession, ConsentStatus, isGatewayUserToken } from '../services/analytics/consent';

export function useProductConsent(token: string | null, email?: string) {
  const [status, setStatus] = useState<ConsentStatus>('loading');
  const [saving, setSaving] = useState(false);
  const [writeError, setWriteError] = useState(false);
  const session = useRef<ConsentSession | undefined>(undefined);
  const lastDecision = useRef(false);

  useLayoutEffect(() => {
    analytics.clearIdentity();
    setStatus('loading');
    setSaving(false);
    setWriteError(false);
    if (!email || !isGatewayUserToken(token, email) || !token) {
      setStatus('error');
      return;
    }
    const current = new ConsentSession(token, email, setStatus);
    session.current = current;
    void current.start();
    return () => {
      current.stop();
      if (session.current === current) session.current = undefined;
    };
  }, [token, email]);

  const decide = useCallback(async (granted: boolean) => {
    lastDecision.current = granted;
    const current = session.current;
    if (!current) {
      setWriteError(true);
      return;
    }
    setSaving(true);
    setWriteError(false);
    try {
      await current.decide(granted ? 'granted' : 'denied');
    } catch {
      // The session already logs the request failure and stops collection.
      if (session.current === current) setWriteError(true);
    } finally {
      if (session.current === current) setSaving(false);
    }
  }, []);

  const retry = () => decide(lastDecision.current);
  return { status, saving, writeError, decide, retry };
}
